/**
 * Phase 27.49.P3 — Load reCAPTCHA v3 once and expose an `execute()`
 * helper that returns a fresh token for a given action.
 *
 * The script is loaded lazily so unrelated pages don't pull it in. Never
 * store the token in React state; get it right before the submit and
 * throw it away afterwards.
 */
import { useCallback, useEffect, useState } from 'react';

const SCRIPT_ID = 'kr-recaptcha-v3';

export default function useRecaptcha(siteKey) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    if (window.grecaptcha?.execute) { setReady(true); return; }
    if (document.getElementById(SCRIPT_ID)) return;

    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      // grecaptcha.ready() fires after the widget is fully wired.
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => setReady(true));
      } else {
        setReady(true);
      }
    };
    document.head.appendChild(s);
  }, [siteKey]);

  const execute = useCallback(async (action) => {
    if (!siteKey) throw new Error('reCAPTCHA site key missing');
    if (!window.grecaptcha?.execute) throw new Error('reCAPTCHA not ready');
    return window.grecaptcha.execute(siteKey, { action });
  }, [siteKey]);

  return { ready, execute };
}
